import { Button } from '@/components/ui/button';
import { useCommonTranslation } from '@/hooks/useTranslation';
import { ChevronLeft, ChevronRight, Send } from 'lucide-react';
import React from 'react';

interface StickyFooterProps {
  onPrevious?: () => void;
  onNext?: () => void;
  onSubmit?: () => void;
  showPrevious?: boolean;
  showNext?: boolean;
  showSubmit?: boolean;
  isValidating?: boolean;
  isSubmitting?: boolean;
  previousText?: string;
  nextText?: string;
  submitText?: string;
  validatingText?: string;
  submittingText?: string;
  className?: string;
}

const StickyFooter: React.FC<StickyFooterProps> = ({
  onPrevious,
  onNext,
  onSubmit,
  showPrevious = false,
  showNext = false,
  showSubmit = false,
  isValidating = false,
  isSubmitting = false,
  previousText,
  nextText,
  submitText,
  validatingText,
  submittingText,
  className,
}) => {
  const { t } = useCommonTranslation();

  // Use translations as defaults when props are undefined
  const defaultPreviousText = t('footer.previous');
  const defaultNextText = t('footer.next');
  const defaultSubmitText = t('footer.submit');
  const defaultValidatingText = t('footer.validating');
  const defaultSubmittingText = t('footer.submitting');

  // Use provided props or fall back to translations
  const finalPreviousText = previousText ?? defaultPreviousText;
  const finalNextText = nextText ?? defaultNextText;
  const finalSubmitText = submitText ?? defaultSubmitText;
  const finalValidatingText = validatingText ?? defaultValidatingText;
  const finalSubmittingText = submittingText ?? defaultSubmittingText;

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 bg-white border-t border-border shadow-lg z-50 ${className || ''}`}
    >
      <div className='container mx-auto p-4'>
        <div className='grid grid-cols-3 items-center gap-4'>
          {/* Previous Button - Left Column */}
          <div className='flex justify-start'>
            {showPrevious && onPrevious && (
              <Button
                onClick={onPrevious}
                disabled={isValidating || isSubmitting}
                size='lg'
                variant='outline'
                className='flex items-center gap-2 min-w-[120px]'
              >
                {isValidating || isSubmitting ? (
                  <>
                    <div className='w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin' />
                    {finalValidatingText}
                  </>
                ) : (
                  <>
                    <ChevronLeft className='w-4 h-4' />
                    {finalPreviousText}
                  </>
                )}
              </Button>
            )}
          </div>

          {/* Submit Button - Center Column */}
          <div className='flex justify-center'>
            {showSubmit && onSubmit && (
              <Button
                onClick={onSubmit}
                disabled={isSubmitting || isValidating}
                size='lg'
                variant='default'
                className='flex items-center gap-2 min-w-[140px]'
              >
                {isSubmitting ? (
                  <>
                    <div className='w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin' />
                    {finalSubmittingText}
                  </>
                ) : (
                  <>
                    <Send className='w-4 h-4' />
                    {finalSubmitText}
                  </>
                )}
              </Button>
            )}
          </div>

          {/* Next Button - Right Column */}
          <div className='flex justify-end'>
            {showNext && onNext && (
              <Button
                onClick={onNext}
                disabled={isValidating || isSubmitting}
                size='lg'
                className='flex items-center gap-2 min-w-[120px]'
              >
                {isValidating || isSubmitting ? (
                  <>
                    <div className='w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin' />
                    {finalValidatingText}
                  </>
                ) : (
                  <>
                    {finalNextText}
                    <ChevronRight className='w-4 h-4' />
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StickyFooter;
